import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AgGridReact } from 'ag-grid-react'
import { ColDef, FilterModel, GridOptions, IDateFilterParams } from 'ag-grid-community'
import { Button, Flex, Space, Spin, Typography } from 'antd'
import { Link } from 'react-router'
import { useListEmployeePublicInfoQuery } from '@/features/employee-data/employee-data.api.ts'
import { plainToInstance } from 'class-transformer'
import dayjs from 'dayjs'
import { DateStringPicker } from '@/common/components/AntdForm/AntdFormDatePicker.tsx'
import { ClearOutlined, FileExcelOutlined, ReloadOutlined } from '@ant-design/icons'
import { handleApiError, isFetchBaseQueryError } from '@/utils/api.tsx'
import { routes, userRoutes } from '@/utils/routes.ts'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { SerializedError } from '@reduxjs/toolkit'
import { BooleanIcon } from '@/common/components/BooleanIcon.tsx'
import { EmployeeTooltip } from '@/common/components/EmployeeTooltip.tsx'
import { EmployeePublicInfo } from '@/features/employee-data/employee-data.def.ts'

const EmployeeNavButton: React.FC<{ name?: string, employeeId: string; toHcView?: boolean}> = ({ employeeId, name = '', toHcView }) => {
  return (
    <Space>
      <Link to={toHcView ? routes.employeeData(employeeId) : userRoutes.employeeProfile(employeeId) }>
        <Button type="link" htmlType="button">
          {name ? `${name}, ${employeeId}` : employeeId}
        </Button>
      </Link>
    </Space>
  )
}

type EmployeeTableOptions = {
  nonHcView?: boolean
  showTodayOnly?: boolean
  defaultColumnNames?: string[]
  defaultFilter?: FilterModel
  getSelectedButton?: {
    text: string
    onClick: (data: EmployeePublicInfo[]) => void
  }
}

type EmployeeTableProps = {
  disableNav?: boolean,
  tableOptions?: EmployeeTableOptions,
  gridOptions?: GridOptions<EmployeePublicInfo>
}

export const dateFilterParams: IDateFilterParams = {
  browserDatePicker: false,
  comparator: (filterLocalDateAtMidnight: Date, cellValue: string) => {
    const val = dayjs(filterLocalDateAtMidnight).format('YYYY-MM-DD')
    if (val == cellValue) return 0

    return val > cellValue ? -1 : 1
  },
}

export const EmployeeTable: React.FC<EmployeeTableProps> = ({
  disableNav = false,
  gridOptions = {},
  tableOptions = {},
}) => {
  const [effectiveDate, setEffectiveDate] = useState<string>(dayjs().format('YYYY-MM-DD'))
  const { data, isFetching, refetch, error } = useListEmployeePublicInfoQuery({ effectiveDate })

  return (
    <RawEmployeeTable
      data={data}
      isFetching={isFetching}
      refetch={refetch}
      error={error}
      disableNav={disableNav}
      gridOptions={gridOptions}
      tableOptions={tableOptions}
      toolbarExtra={!tableOptions.showTodayOnly ?
        <Flex gap="small" align="center">
          <Typography.Text>Effective Date</Typography.Text>
          <DateStringPicker
            allowClear={false}
            value={effectiveDate}
            onChange={val => setEffectiveDate(val || dayjs().format('YYYY-MM-DD'))}
          />
        </Flex>
        : null}
    />
  )
}

type RawEmployeeTableProps = EmployeeTableProps & {
  data?: EmployeePublicInfo[]
  isFetching: boolean
  refetch: () => void
  error?: FetchBaseQueryError | SerializedError
  toolbarExtra?: React.ReactNode
}

export const RawEmployeeTable: React.FC<RawEmployeeTableProps> = ({
  data,
  isFetching,
  refetch,
  error,
  toolbarExtra,
  disableNav = false,
  gridOptions = {},
  tableOptions = {},
}) => {

  useEffect(() => {
    if (error && isFetchBaseQueryError(error)) {
      handleApiError(error)
    }
  }, [error])

  const applyDefaultFilter = useCallback(() => {
    if (tableOptions.defaultFilter && Object.keys(tableOptions.defaultFilter).length) {
      gridRef.current?.api.setFilterModel(tableOptions.defaultFilter)
      gridRef.current?.api.onFilterChanged()
    }
  }, [tableOptions.defaultFilter])

  const getInitialHide = useCallback((name: string, defaultVal = false) => {
    if (!tableOptions.defaultColumnNames) return defaultVal

    return !tableOptions.defaultColumnNames.includes(name)
  }, [tableOptions.defaultColumnNames])

  const rowData = useMemo(() => {
    return data?.map(x => plainToInstance(EmployeePublicInfo, x)) || []
  }, [data])

  const gridRef = useRef<AgGridReact<EmployeePublicInfo>>(null)

  const columnDefs: GridOptions<EmployeePublicInfo>['columnDefs'] = useMemo(() => [
    {
      colId: 'employeeId',
      field: 'employeeId',
      headerName: 'Employee ID',
      initialHide: getInitialHide('employeeId'),
      cellRenderer: ({ value }: { value: string }) => disableNav ? value : <EmployeeNavButton toHcView={!tableOptions.nonHcView} employeeId={value} />,
    },
    {
      colId: 'displayName',
      field: 'displayName',
      headerName: 'Display Name',
      enableRowGroup: true,
      initialHide: getInitialHide('displayName'),
      cellRenderer: ({ data }: { data: EmployeePublicInfo }) => <EmployeeTooltip
        employeeId={data?.employeeId}
      />,
    },
    { colId: 'username', field: 'username', headerName: 'Username', initialHide: getInitialHide('username', true) },
    {
      colId: 'companyEmail',
      field: 'companyEmail',
      headerName: 'Company Email',
      initialHide: getInitialHide('companyEmail', true),
    },
    {
      colId: 'title',
      field: 'title',
      headerName: 'Title',
      initialHide: getInitialHide('title'),
      enableRowGroup: true,
    },
    {
      colId: 'country',
      field: 'country',
      headerName: 'Country',
      initialHide: getInitialHide('country'),
      enableRowGroup: true,
      enablePivot: true,
    },
    {
      colId: 'department',
      field: 'department',
      headerName: 'Department',
      initialHide: getInitialHide('department'),
      enableRowGroup: true,
      enablePivot: true,
    },
    {
      colId: 'subDepartment',
      field: 'subDepartment',
      headerName: 'Sub Department',
      initialHide: getInitialHide('subDepartment'),
      enableRowGroup: true,
      enablePivot: true,
    },
    {
      colId: 'subGroup',
      field: 'subGroup',
      headerName: 'Sub Group',
      initialHide: getInitialHide('subGroup', true),
      enableRowGroup: true,
      enablePivot: true,
    },
    {
      colId: 'officeLocation.city',
      field: 'officeLocation.city',
      headerName: 'Office - City',
      initialHide: getInitialHide('officeLocation.city', true),
      enableRowGroup: true,
      enablePivot: true,
    },
    {
      colId: 'businessUnit',
      field: 'businessUnit',
      headerName: 'Business Unit',
      initialHide: getInitialHide('businessUnit', true),
      enableRowGroup: true,
      enablePivot: true,
    },
    {
      colId: 'position',
      field: 'position',
      headerName: 'Position',
      initialHide: getInitialHide('position'),
      enableRowGroup: true,
      enablePivot: true,
    },
    {
      colId: 'reportingPmCode',
      field: 'reportingPmCode',
      headerName: 'Reporting PM Code',
      initialHide: getInitialHide('reportingPmCode', true),
      enableRowGroup: true,
      enablePivot: true,
    },
    {
      colId: 'gender',
      field: 'gender',
      headerName: 'Gender',
      initialHide: getInitialHide('gender', true),
      enableRowGroup: true,
      enablePivot: true,
    },
    {
      colId: 'birthday',
      field: 'birthday',
      headerName: 'Birthday',
      initialHide: getInitialHide('birthday', true),
      valueFormatter: ({ value }) => value ? dayjs(value).format('D MMM') : '',
    },
    {
      colId: 'joinDate',
      field: 'joinDate',
      filter: 'agDateColumnFilter',
      initialHide: getInitialHide('joinDate'),
      filterParams: dateFilterParams,
      headerName: 'Join Date',
      valueFormatter: ({ value }) => value,
    },
    {
      colId: 'employmentType',
      field: 'employmentType',
      headerName: 'Employment Type',
      initialHide: getInitialHide('employmentType', true),
      enableRowGroup: true,
      enablePivot: true,
    },
    {
      colId: 'employeeType',
      field: 'employeeType',
      headerName: 'Employee Type',
      initialHide: getInitialHide('employeeType', true),
      enableRowGroup: true,
      enablePivot: true,
    },
    {
      colId: 'employmentStatus',
      field: 'employmentStatus',
      headerName: 'Employment Status',
      initialHide: getInitialHide('employmentStatus', true),
      enableRowGroup: true,
      enablePivot: true,
    },
    {
      colId: 'pmCode',
      field: 'pmCode',
      headerName: 'PM Code',
      initialHide: getInitialHide('pmCode', true),
      enableRowGroup: true,
      enablePivot: true,
    },
    {
      colId: 'reportingManager',
      headerName: 'Reporting Manager',
      initialHide: getInitialHide('reportingManager', true),
      enableRowGroup: true,
      enablePivot: true,
      valueGetter: params => params.data?.reportingManagerName,
      cellRenderer: ({ data }: { data: EmployeePublicInfo }) => data.reportingManager ?
        <EmployeeTooltip employeeId={data.reportingManager} /> : null,
    },
    {
      colId: 'hasCompletedInfo',
      field: 'hasCompletedInfo',
      headerName: 'Has Completed Info',
      initialHide: getInitialHide('hasCompletedInfo', true),
      enableRowGroup: true,
      cellRenderer: ({ value }: { value: boolean }) => <BooleanIcon value={value} />,
    },
  ], [disableNav, getInitialHide])

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      flex: 1,
      minWidth: 150,
      filter: true,
      enableRowGroup: true,
    }
  }, [])

  const autoGroupColumnDef = useMemo(() => {
    return {
      minWidth: 250,
    }
  }, [])

  return (
    <Flex vertical gap="middle">
      <Flex gap="middle" align="flex-end">
        {toolbarExtra}
        <Button onClick={refetch} htmlType="button" icon={<ReloadOutlined />}>Refresh</Button>
        <Button
          onClick={() => gridRef.current?.api.exportDataAsExcel()}
          htmlType="button"
          icon={<FileExcelOutlined />}
        >
          Export
        </Button>
        <Button
          onClick={() => {
            gridRef.current?.api.setFilterModel(null)
            gridRef.current?.api.onFilterChanged()
          }}
          htmlType="button"
          icon={<ClearOutlined />}
        >
          Reset Filter
        </Button>
        <div className="flex-1" />
        {tableOptions.getSelectedButton ?
          <Button
            type="primary"
            onClick={() => tableOptions.getSelectedButton?.onClick(gridRef.current?.api.getSelectedRows() || [])}
            htmlType="button"
          >
            {tableOptions.getSelectedButton?.text}
          </Button> : null}
      </Flex>
      <div className="w-full h-[580px]">
        <Spin spinning={isFetching} fullscreen />
        <div className="ag-theme-quartz w-full h-full">
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            onRowDataUpdated={applyDefaultFilter}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            autoGroupColumnDef={autoGroupColumnDef}
            rowGroupPanelShow="always"
            sideBar={['columns', 'filters']}
            pagination={false}
            suppressContextMenu
            {...gridOptions}
          />
        </div>
      </div>
    </Flex>
  )
}